import { TINFormIndiaNew } from '@/components/admin'
import Layout from '@/components/layout/Layout'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import {
    type MTTinField,
    type MTTinFieldConfig,
} from '@/multi-territory/mt-configs/mt-tin-field-configs'
import { getEntityDropdownByCountry } from '@/multi-territory/mt-services/mt-assign-entity-api'
import { getEntityCountryDropdown } from '@/multi-territory/mt-services/mt-entity-api'
import {
    fetchTanMasterMetadata,
    getTanMasterById,
    saveTanMaster,
    updateTanMaster,
} from '@/multi-territory/mt-services/mt-tan-master-api'
import { sanitizeForLog } from '@/multi-territory/mt-utils/mt-sanitize'
import { ROUTES } from '@/routes/routeConfig'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import MTCustomSelect from '../mt-shared/MTCustomSelect'

const EntitySelect = MTCustomSelect as any

// Converts a simple regex like ^[A-Z]{4}[0-9]{4}$$ into a format hint like "AAAA0000"
function getRegexFormatHint(regex: string): string {
  const clean = regex.replace(/^\^/, '').replace(/\$+$/, '')
  let hint = ''
  const tokenRe =
    /\[([^\]]+)\]\{(\d+)\}|\[([^\]]+)\]|([A-Za-z0-9])\{(\d+)\}|([A-Za-z0-9])/g
  let match: RegExpExecArray | null
  while ((match = tokenRe.exec(clean)) !== null) {
    if (match[1] && match[2]) {
      // [A-Z]{4} or [0-9]{4}
      const char = match[1].includes('A-Z')
        ? 'A'
        : match[1].includes('a-z')
          ? 'a'
          : match[1].includes('0-9')
            ? '0'
            : match[1][0]
      hint += char.repeat(Number(match[2]))
    } else if (match[3]) {
      // [A-Z] without count
      const char = match[3].includes('A-Z')
        ? 'A'
        : match[3].includes('a-z')
          ? 'a'
          : match[3].includes('0-9')
            ? '0'
            : match[3][0]
      hint += char
    } else if (match[4] && match[5]) {
      hint += match[4].repeat(Number(match[5]))
    } else if (match[6]) {
      hint += match[6]
    }
  }
  return hint
}

interface CountryOption {
  uuid: string
  countryCode: string
  countryName: string
}

interface EntityOption {
  uuid: string
  entityName: string
  uniqueIdentificationNumber?: string
}

export function MTAdminTinForm() {
  const navigate = useNavigate()
  const location = useLocation()

  // Get state from navigation (passed by list page)
  const navState = location.state as {
    editMode?: boolean
    uuid?: string
    countryCode?: string
    countryName?: string
    entityMasterUuid?: string
    attributes?: Record<string, unknown>
  } | null

  const isEditMode = navState?.editMode ?? false
  const editUuid = navState?.uuid as string | undefined

  // Filter state (for create mode)
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([])
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([])
  const [selectedCountry, setSelectedCountry] = useState<string>(
    navState?.countryCode ?? ''
  )
  const [selectedEntity, setSelectedEntity] = useState<string>(
    navState?.entityMasterUuid ?? ''
  )
  const [applied, setApplied] = useState(false)

  // Form state
  const [fieldConfig, setFieldConfig] = useState<MTTinFieldConfig | null>(null)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [addressSameAsAbove, setAddressSameAsAbove] = useState(false)
  const [loading, setLoading] = useState(false)
  const { showSnackbar } = useSnackbar()

  // Fetch country dropdown on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const resp = await getEntityCountryDropdown()
        if (resp.success && Array.isArray(resp.data) && resp.data.length > 0) {
          const filtered = resp.data
            .filter(item => item.tax_registration_required !== false)
            .map(item => ({
              uuid: item.uuid,
              countryCode: item.country_code || item.country_name,
              countryName: item.country_name,
            }))
          setCountryOptions(filtered)
          // Reset selectedCountry if it's not in the filtered list
          setSelectedCountry(prev => {
            if (prev && !filtered.some(c => c.countryCode === prev)) return ''
            return prev
          })
        }
      } catch {
        // fallback
      }
    }
    fetchCountries()
  }, [])

  // Fetch entity dropdown when country changes
  useEffect(() => {
    if (!selectedCountry || countryOptions.length === 0) {
      setEntityOptions([])
      return
    }

    const countryOpt = countryOptions.find(
      c => c.countryCode === selectedCountry
    )
    if (!countryOpt) return

    const fetchEntities = async () => {
      try {
        const result = await getEntityDropdownByCountry([countryOpt.uuid])
        if (result.success && Array.isArray(result.data)) {
          setEntityOptions(
            result.data.map(item => ({
              uuid: item.uuid,
              entityName: item.entityName,
              uniqueIdentificationNumber: item.uniqueIdentificationNumber,
            }))
          )
        }
      } catch {
        setEntityOptions([])
      }
    }
    fetchEntities()
  }, [selectedCountry, countryOptions])

  // Fetch metadata and initialize form
  const loadMetadata = useCallback(
    async (
      country: string,
      attributes?: Record<string, unknown>,
      entityName?: string,
      uniqueIdentificationNumber?: string
    ) => {
      if (!country) return

      setLoading(true)
      try {
        const result = await fetchTanMasterMetadata<MTTinFieldConfig>(country)

        if (result.success && result.data) {
          const config = result.data

          // Enrich fields: for required fields with maxLength but no minLength
          // and no regex, set minLength = maxLength (fixed-length identifiers)
          config.fields = config.fields.map(field => {
            if (
              !field.system &&
              (field.required || field.validations.required) &&
              field.validations.maxLength &&
              !field.validations.minLength &&
              !field.validations.regex &&
              field.validations.maxLength <= 20
            ) {
              return {
                ...field,
                validations: {
                  ...field.validations,
                  minLength: field.validations.maxLength,
                },
              }
            }
            return field
          })

          setFieldConfig(config)

          const initialData: Record<string, string> = {}
          config.fields
            .filter(f => !f.system)
            .forEach(field => {
              initialData[field.key] = field.validations.defaultValue ?? ''
            })

          // In edit mode, override with existing attribute values
          if (attributes) {
            config.fields
              .filter(f => !f.system)
              .forEach(field => {
                const val = attributes[field.key]
                if (val !== undefined && val !== null) {
                  initialData[field.key] = String(val)
                }
              })
          }

          // Auto-fill entity name and unique identification number from selected entity dropdown
          if (entityName) {
            const entityNameField = config.fields.find(
              f =>
                f.key === 'entity_name' ||
                f.key === 'entity_namee' ||
                (f.key && f.key.toLowerCase().includes('entity_name'))
            )
            if (entityNameField) {
              initialData[entityNameField.key] = entityName
            }
          }
          if (uniqueIdentificationNumber) {
            const uinField = config.fields.find(
              f => f.key === 'unique_identification_number'
            )
            if (uinField) {
              initialData[uinField.key] = uniqueIdentificationNumber
            }
          }

          setFormData(initialData)
          setErrors({})
          setTouched({})
          setAddressSameAsAbove(false)
          setApplied(true)
        } else {
          // CWE-117: sanitize untrusted error payload before logging.
          console.error(
            'Failed to fetch metadata:',
            sanitizeForLog(!result.success && result.error)
          )
        }
      } catch (error) {
        console.error('Error fetching metadata:', sanitizeForLog(error))
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Detect whether the currently selected country is India (by code or name).
  // Codes/names in this environment can be non-standard (e.g. "IND", "India2"),
  // so match the India code (IN/IND*) or a name that starts with "INDIA".
  const isIndiaCountry = (code: string): boolean => {
    const name = countryOptions.find(c => c.countryCode === code)?.countryName
    const codeVal = String(code ?? '')
      .trim()
      .toUpperCase()
    const nameVal = String(name ?? '')
      .trim()
      .toUpperCase()
    // Debug: confirm the actual selected country code/name once.
    console.log('[MT TIN] India check ->', { code: codeVal, name: nameVal })
    const codeIsIndia = codeVal === 'IN' || codeVal.startsWith('IND')
    const nameIsIndia = nameVal.startsWith('INDIA')
    return codeIsIndia || nameIsIndia
  }

  // Handle Apply button (create mode)
  const handleApply = () => {
    if (!selectedCountry || !selectedEntity) return
    // India does NOT use the metadata-driven form. Clicking Apply simply
    // switches the page over to the dedicated <TANForm /> (rendered below).
    if (isIndiaCountry(selectedCountry)) {
      setApplied(true)
      return
    }
    const selectedEntityObj = entityOptions.find(e => e.uuid === selectedEntity)
    loadMetadata(
      selectedCountry,
      undefined,
      selectedEntityObj?.entityName,
      selectedEntityObj?.uniqueIdentificationNumber
    )
  }

  // Auto-load in edit mode: fetch TAN record from API
  useEffect(() => {
    if (!isEditMode || !selectedCountry || !editUuid) return
    // India edit is handled entirely by <TANForm /> — do not call the MT
    // metadata / record APIs for India.
    if (isIndiaCountry(selectedCountry)) return

    const fetchEditData = async () => {
      setLoading(true)
      try {
        const result = await getTanMasterById<{
          uuid: string
          countryCode: string
          entityMasterUuid: string
          entityName: string
          attributes: Record<string, unknown>
        }>(selectedCountry, editUuid)

        if (result.success && result.data) {
          const record = result.data
          // Set entity from API response
          if (record.entityMasterUuid) {
            setSelectedEntity(record.entityMasterUuid)
          }
          // Load metadata and fill form with attributes from API
          // unique_identification_number comes from attributes; entityName from record
          await loadMetadata(
            selectedCountry,
            record.attributes,
            record.entityName
          )
        } else {
          // CWE-117: sanitize untrusted error payload before logging.
          console.error(
            'Failed to fetch TAN record:',
            sanitizeForLog(!result.success && result.error)
          )
          // Fallback to navState attributes if API fails
          loadMetadata(selectedCountry, navState?.attributes)
        }
      } catch (error) {
        console.error('Error fetching TAN record:', sanitizeForLog(error))
        loadMetadata(selectedCountry, navState?.attributes)
      } finally {
        setLoading(false)
      }
    }

    fetchEditData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedCountry, editUuid])

  // Validate a single field
  const validateField = useCallback(
    (field: MTTinField, value: string): string => {
      const val = value?.trim() ?? ''

      // Check required: respect both field.required and field.validations.required
      if ((field.required || field.validations.required) && !val) {
        return `${field.label} is required`
      }

      if (
        val &&
        field.validations.minLength &&
        val.length < field.validations.minLength
      ) {
        return `${field.label} must be at least ${field.validations.minLength} characters`
      }

      if (
        val &&
        field.validations.maxLength &&
        val.length > field.validations.maxLength
      ) {
        return `${field.label} cannot exceed ${field.validations.maxLength} characters`
      }

      if (val && field.validations.regex) {
        try {
          const regex = new RegExp(field.validations.regex)
          if (!regex.test(val)) {
            if (field.validations.regexMessage) {
              return field.validations.regexMessage
            }
            const hint = getRegexFormatHint(field.validations.regex)
            return `Invalid ${field.label} format${hint ? `. Expected format: ${hint}` : ''}`
          }
        } catch {
          // If regex is malformed, skip regex validation
        }
      }

      if (val && field.validations.invalidValues) {
        if (field.validations.invalidValues.includes(val.toUpperCase())) {
          return `This value is not accepted`
        }
      }

      return ''
    },
    []
  )

  // Handle field change
  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }))
    setTouched(prev => ({ ...prev, [key]: true }))

    if (fieldConfig) {
      const field = fieldConfig.fields.find(f => f.key === key)
      if (field) {
        const error = validateField(field, value)
        setErrors(prev => {
          const updated = { ...prev }
          if (error) {
            updated[key] = error
          } else {
            delete updated[key]
          }
          return updated
        })
      }
    }
  }

  // Handle blur
  const handleBlur = (key: string, value: string) => {
    setTouched(prev => ({ ...prev, [key]: true }))
    if (fieldConfig) {
      const field = fieldConfig.fields.find(f => f.key === key)
      if (field) {
        const error = validateField(field, value)
        setErrors(prev => {
          const updated = { ...prev }
          if (error) {
            updated[key] = error
          } else {
            delete updated[key]
          }
          return updated
        })
      }
    }
  }

  // Handle "Address same as above" checkbox
  // Builds copy mapping dynamically: for API configs (tan_group), match by stripping
  // 'responsible_person_details_' prefix; for static configs, use validations.copyFrom
  const handleAddressSameAsAbove = (checked: boolean) => {
    setAddressSameAsAbove(checked)
    if (!fieldConfig) return

    const rpFields = fieldConfig.fields.filter(
      f =>
        f.section === 'responsible_person' ||
        (f as any).tan_group === 'responsible_person_details'
    )

    const entityKeys = new Set(
      fieldConfig.fields
        .filter(
          f =>
            (!f.section && !(f as any).tan_group) ||
            (f as any).tan_group === 'entity_details'
        )
        .map(f => f.key)
    )

    const getCopyFromKey = (rpField: MTTinField): string | undefined => {
      // Static config: use copyFrom
      if (rpField.validations.copyFrom) return rpField.validations.copyFrom
      // API config: strip 'responsible_person_details_' prefix and check if entity field exists
      const stripped = rpField.key.replace(/^responsible_person_details_/, '')
      if (stripped !== rpField.key && entityKeys.has(stripped)) return stripped
      return undefined
    }

    const newData = { ...formData }
    rpFields.forEach(rpField => {
      const sourceKey = getCopyFromKey(rpField)
      if (!sourceKey) return
      newData[rpField.key] = checked ? formData[sourceKey] || '' : ''
    })
    setFormData(newData)
  }

  // Helper: determine if a field is disabled (should skip validation)
  const isFieldDisabled = (field: MTTinField): boolean => {
    const isEntityNameField =
      field.key === 'entity_name' ||
      field.key === 'entity_namee' ||
      field.key.toLowerCase().includes('entity_name')
    const isUinField = field.key === 'unique_identification_number'
    const isLockedInEdit =
      field.key === 'pan_number' ||
      field.key === 'tax_deduction_account_numbers' ||
      field.key === 'tax_identification_number'
    return (
      !!field.system ||
      isEntityNameField ||
      isUinField ||
      (isLockedInEdit && isEditMode)
    )
  }

  // Validate entire form
  const validateForm = (): boolean => {
    if (!fieldConfig) return false

    const newErrors: Record<string, string> = {}
    const newTouched: Record<string, boolean> = {}
    let isValid = true

    fieldConfig.fields
      .filter(f => !f.system && f.visible && !isFieldDisabled(f))
      .forEach(field => {
        newTouched[field.key] = true
        const error = validateField(field, formData[field.key] || '')
        if (error) {
          newErrors[field.key] = error
          isValid = false
        }
      })

    fieldConfig.fields
      .filter(f => f.validations.pairedWith)
      .forEach(field => {
        const val = (formData[field.key] || '').trim()
        const pairedVal = (formData[field.validations.pairedWith!] || '').trim()
        if ((val && !pairedVal) || (!val && pairedVal)) {
          newErrors[field.key] =
            field.validations.pairedMessage ||
            'Both fields must be filled or both empty'
          isValid = false
        }
      })

    fieldConfig.fields
      .filter(f => f.validations.crossFieldValidation)
      .forEach(field => {
        const val = (formData[field.key] || '').trim()
        const otherKey = field.validations.crossFieldValidation!.notEqualTo
        const otherVal = (formData[otherKey] || '').trim()
        if (val && otherVal && val === otherVal) {
          newErrors[field.key] = field.validations.crossFieldValidation!.message
          isValid = false
        }
      })

    setErrors(newErrors)
    setTouched(prev => ({ ...prev, ...newTouched }))
    return isValid
  }

  // Handle form submit
  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const attributes: Record<string, string | number | boolean> = {}
      fieldConfig!.fields
        .filter(f => !f.system && f.visible)
        .forEach(field => {
          const value = formData[field.key]
          if (value !== undefined && value !== '') {
            attributes[field.key] = value
          }
        })

      let result
      if (isEditMode && editUuid) {
        result = await updateTanMaster({
          uuid: editUuid,
          countryCode: selectedCountry,
          entityMasterUuid: selectedEntity,
          attributes,
        })
      } else {
        result = await saveTanMaster({
          countryCode: selectedCountry,
          entityMasterUuid: selectedEntity,
          attributes,
        })
      }

      if (result.success) {
        navigate(ROUTES.MT_ADMIN_TAX_IDENTIFICATION_NUMBER)
        showSnackbar(result.data, 'success')
      } else {
        showSnackbar(result.error ?? 'Failed to save', 'error')
      }
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'An unexpected error occurred',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.MT_ADMIN_TAX_IDENTIFICATION_NUMBER)
  }

  // Show "Address same as above" only when there are copyable address fields
  const hasCopyableAddressFields = useMemo(() => {
    if (!fieldConfig) return false
    const entityKeys = new Set(
      fieldConfig.fields
        .filter(
          f =>
            (f as any).tan_group === 'entity_details' ||
            (!f.section && !(f as any).tan_group)
        )
        .map(f => f.key)
    )
    return fieldConfig.fields
      .filter(
        f =>
          f.section === 'responsible_person' ||
          (f as any).tan_group === 'responsible_person_details'
      )
      .some(f => {
        if (f.validations.copyFrom) return true
        const stripped = f.key.replace(/^responsible_person_details_/, '')
        return stripped !== f.key && entityKeys.has(stripped)
      })
  }, [fieldConfig])

  // Split fields into sections using tan_group (from API) or section (from static config)
  const entityFields = useMemo(
    () =>
      fieldConfig?.fields.filter(f => {
        const tanGroup = (f as any).tan_group
        if (tanGroup)
          return tanGroup === 'entity_details' && !f.system && f.visible
        return !f.section && !f.system && f.visible
      }) || [],
    [fieldConfig]
  )

  const responsiblePersonFields = useMemo(
    () =>
      fieldConfig?.fields.filter(f => {
        const tanGroup = (f as any).tan_group
        if (tanGroup)
          return (
            tanGroup === 'responsible_person_details' && !f.system && f.visible
          )
        return f.section === 'responsible_person' && !f.system && f.visible
      }) || [],
    [fieldConfig]
  )

  // Convert MTTinField to MTMetadataFormField for rendering via MTFormField
  // Render a single field (horizontal label + input layout)
  const renderField = (field: MTTinField) => {
    const value = formData[field.key] || ''
    const rawError = touched[field.key] ? errors[field.key] : ''
    const isDisabled = isFieldDisabled(field)
    const error = isDisabled ? '' : rawError
    const isDropdown = field.type === 'dropdown' && Array.isArray(field.options)
    const isNumber = field.type === 'number' || field.dataType === 'DECIMAL'

    return (
      <Box
        key={field.key}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          sx={{
            minWidth: { xs: 'auto', sm: '180px', md: '200px' },
            fontSize: '14px',
            fontWeight: 400,
          }}
        >
          {field.label}
          {(field.required || field.validations.required) && (
            <span style={{ color: 'var(--forbrand-error, #d32f2f)' }}>*</span>
          )}
        </Typography>

        {isDropdown ? (
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Select
              value={value}
              error={!!error}
              onChange={e => handleChange(field.key, e.target.value as string)}
              disabled={isDisabled}
              displayEmpty
              fullWidth
              title={
                value
                  ? (field.options || []).find(o => o.value === value)?.label ||
                    value
                  : ''
              }
              renderValue={selected => {
                if (!selected) {
                  return (
                    <span style={{ color: '#aaa' }}>
                      {field.placeholder ||
                        `Select ${field.label.toLowerCase()}`}
                    </span>
                  )
                }
                const opt = (field.options || []).find(
                  o => o.value === selected
                )
                return opt ? opt.label : selected
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                border: '1px solid #E7E7E7',
                height: '40px',
                fontSize: '14px',
                minWidth: 0,
                width: '100%',
                overflow: 'hidden',
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  maxWidth: '100%',
                },
                '&.Mui-disabled': { backgroundColor: '#f5f5f5' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    minWidth: '280px !important',
                    maxWidth: '400px',
                  },
                },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}
            >
              {(field.options || []).map(opt => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{ whiteSpace: 'normal', lineHeight: 1.5, py: 1 }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {error && (
              <FormHelperText
                error
                sx={{ margin: 0, marginTop: '4px', fontSize: '12px' }}
              >
                {error}
              </FormHelperText>
            )}
          </Box>
        ) : (
          <TextField
            fullWidth
            value={value}
            onChange={e => {
              const v = e.target.value
              if (isNumber && v !== '' && !/^[0-9]*\.?[0-9]*$/.test(v)) return
              handleChange(field.key, v)
            }}
            onBlur={e => handleBlur(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            disabled={isDisabled}
            placeholder={
              field.placeholder || `Enter ${field.label.toLowerCase()}`
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: error ? '1px solid #d32f2f' : 'none',
                },
                border: error ? 'none' : '1px solid #E7E7E7',
                height: '40px',
                fontSize: '14px',
                '&.Mui-disabled': { backgroundColor: '#f5f5f5' },
                '&.Mui-error': {
                  border: 'none',
                  '& fieldset': {
                    border: '1px solid #d32f2f',
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: '#d32f2f',
                fontSize: '12px',
                marginLeft: 0,
              },
            }}
            slotProps={{
              formHelperText: { sx: { margin: 0, marginTop: '4px' } },
              htmlInput: {
                maxLength: field.validations.maxLength || undefined,
              },
            }}
          />
        )}
      </Box>
    )
  }

  // Render fields using grid_column pairing (API) or index pairing (static config)
  const renderFieldRows = (fields: MTTinField[]) => {
    const hasGridColumn = fields.some(f => (f as any).grid_column != null)

    let rows: MTTinField[][]
    if (hasGridColumn) {
      // Group by order: grid_column=1 starts a new row, grid_column=2 is right column
      rows = []
      let currentRow: MTTinField[] = []
      fields.forEach(f => {
        const col = (f as any).grid_column ?? 1
        if (col === 1) {
          if (currentRow.length) rows.push(currentRow)
          currentRow = [f]
        } else {
          currentRow.push(f)
        }
      })
      if (currentRow.length) rows.push(currentRow)
    } else {
      rows = []
      for (let i = 0; i < fields.length; i += 2) {
        rows.push(fields.slice(i, i + 2))
      }
    }

    return rows.map((row, idx) => (
      <Box
        key={idx}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        {row.map(field => renderField(field))}
        {row.length === 1 && <Box sx={{ flex: 1 }} />}
      </Box>
    ))
  }

  // India uses the dedicated India TAN form (its own India APIs, entity
  // dropdown, CSI upload and validation). In create mode we only switch to
  // <TANForm /> after the user clicks Apply (applied === true); in edit mode
  // we render it straight away. No metadata API is called for India.
  // Detect India from the selected country OR the country name passed by the
  // list page (edit mode), so India rows always route to <TANForm />.
  const navCountryName = (navState?.countryName ?? '').trim().toUpperCase()
  const isIndiaSelected =
    isIndiaCountry(selectedCountry) ||
    navCountryName === 'IN' ||
    navCountryName === 'INDIA'

  // Entity chosen in the MT filter bar (used to prefill the India TIN form).
  const selectedIndiaEntityObj = entityOptions.find(
    e => e.uuid === selectedEntity
  )
  const showIndiaTinForm = isIndiaSelected && (isEditMode || applied)

  return (
    <Layout hideFooter>
      <Box sx={{ pb: '80px' }}>
        {/* Page Title */}
        <Box
          sx={{
            borderTop: '1px solid #E7E7E7',
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500, fontSize: '20px' }}>
            {isEditMode
              ? 'Edit Tax Identification Master'
              : 'Create Tax Identification Master'}
          </Typography>
        </Box>

        {/* Filter Bar: Country + Entity + Apply (visible in create mode, hidden in edit) */}
        {!isEditMode && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: { xs: 2, sm: 3 },
              py: 2,
              flexWrap: 'wrap',
            }}
          >
            <EntitySelect
              value={selectedCountry}
              onChange={(e: { target: { value: string } }) => {
                setSelectedCountry(e.target.value)
                setSelectedEntity('')
                setApplied(false)
                setFieldConfig(null)
              }}
              options={countryOptions}
              getOptionLabel={(option: CountryOption) => option.countryName}
              getOptionValue={(option: CountryOption) => option.countryCode}
              placeholder="Select country"
            />

            <EntitySelect
              value={selectedEntity}
              onChange={(e: { target: { value: string } }) => {
                setSelectedEntity(e.target.value)
                setApplied(false)
                setFieldConfig(null)
              }}
              options={entityOptions}
              getOptionLabel={(option: EntityOption) => option.entityName}
              getOptionValue={(option: EntityOption) => option.uuid}
              placeholder="Select entity"
              searchable
              searchPlaceholder="Search entity"
              disabled={!selectedCountry}
            />

            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!selectedCountry || !selectedEntity || loading}
              sx={{
                backgroundColor: 'var(--forbrand-bg-variant, #FDE4C5)',
                color: 'var(--forbrand-brand-varient, #D93954)',
                textTransform: 'none',
                fontWeight: 500,
                height: '44px',
                px: 4,
                '&:hover': { backgroundColor: '#fdd9ae' },
                '&:disabled': { backgroundColor: '#eee', color: '#aaa' },
              }}
            >
              {loading && !applied ? 'Loading...' : 'Apply'}
            </Button>
          </Box>
        )}

        {/* India: dedicated India TIN form rendered below the select bar */}
        {showIndiaTinForm && (
          <TINFormIndiaNew
            embedded
            returnTo={ROUTES.MT_ADMIN_TAX_IDENTIFICATION_NUMBER}
            editId={isEditMode ? editUuid : undefined}
            presetEntityUuid={isEditMode ? undefined : selectedEntity}
            presetEntityName={
              isEditMode
                ? (navState?.attributes?.entityName as string | undefined)
                : selectedIndiaEntityObj?.entityName
            }
            presetPan={
              isEditMode
                ? (navState?.attributes?.uniqueIdentificationNumber as
                    | string
                    | undefined)
                : selectedIndiaEntityObj?.uniqueIdentificationNumber
            }
            presetCountryCode={selectedCountry}
          />
        )}

        {/* Dynamic Form - shown after Apply (create) or auto-loaded (edit) */}
        {!isIndiaSelected && applied && fieldConfig && (
          <>
            {/* Entity Details Section */}
            <Box
              sx={{
                borderTop: '1px solid #E7E7E7',
                px: { xs: 2, sm: 3 },
                py: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, fontSize: '16px' }}
              >
                Entity details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {renderFieldRows(entityFields)}
              </Box>
            </Box>

            {/* Responsible Person Details Section */}
            {responsiblePersonFields.length > 0 && (
              <Box
                sx={{
                  borderTop: '1px solid var(--border-subtel)',
                  px: { xs: 2, sm: 3 },
                  py: 3,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: '16px' }}
                  >
                    Responsible person details
                  </Typography>
                  {hasCopyableAddressFields && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addressSameAsAbove}
                          onChange={e =>
                            handleAddressSameAsAbove(e.target.checked)
                          }
                          sx={{
                            '&.Mui-checked': {
                              color: 'var(--forbrand-color, #D93954)',
                            },
                            color: 'var(--forbrand-color, #D93954)',
                          }}
                        />
                      }
                      label="Address same as above"
                      sx={{ fontSize: '14px' }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {renderFieldRows(responsiblePersonFields)}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Action Buttons - hidden for India (embedded form has its own) */}
        {!showIndiaTinForm && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: '1px solid var(--border-subtel)',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#ffffff',
              zIndex: 10,
            }}
          >
            <Button
              onClick={handleCancel}
              disabled={loading}
              sx={{
                textTransform: 'none',
                minWidth: 100,
                color: 'var(--forbrand-brand-varient)',
                fontSize: '16px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !fieldConfig}
              sx={{
                textTransform: 'none',
                minWidth: 150,
                backgroundColor: 'var(--forbrand-bg-variant, #FDE4C5)',
                color: 'var(--forbrand-brand-varient, #D93954)',
                fontSize: '16px',
                '&:hover': { backgroundColor: '#fdd9ae' },
                '&:disabled': { backgroundColor: '#ccc' },
              }}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        )}
      </Box>
    </Layout>
  )
}
